// The visible way out of a bee pick (WAG-36, `bee-state-flash` task 9.4).
//
// A pick is made by tapping a bee and cleared by tapping the *same* bee again — which is a trap at
// density: the field moves, the crowd bound drops the labels around it, and re-finding one 24 px
// sprite among hundreds to undo a tap is not an affordance. So a pick always shows this, and it
// says which bee is picked as well as offering the way out.
//
// Shared by both rounds playback screens rather than written twice: the two must behave and read
// identically, and each screen supplies only its own `data-testid` and where it sits — placement is
// the screen's business (their HUDs are laid out differently), the chip itself is not.
//
// It costs the layout **nothing**: it is an absolutely-positioned overlay inside the world view,
// mounted only while a pick exists, so the exactly-accounted portrait height budget (UI.md §4B —
// a 212 px control stack, the world taking the remainder) is untouched.

import type { CSSProperties } from 'react';

export interface PickedBeeChipProps {
  /** The picked bee, named so the chip is a readout as well as a control. */
  beeId: number;
  onClear: () => void;
  /** The screen's own test hook — the two screens are asserted separately. */
  testId: string;
  /** Where the screen puts it, merged over the chip's own chrome. */
  style?: CSSProperties;
}

export function PickedBeeChip({ beeId, onClear, testId, style }: PickedBeeChipProps) {
  return (
    <button
      type="button"
      data-testid={testId}
      // A press on floating HUD chrome is that chrome's press, never a canvas tap — otherwise
      // clearing the pick would immediately pick whatever bee happens to be under the chip. A
      // `<button>` already opts out in `useCanvasCameraGestures`; the attribute states it anyway.
      data-gesture-chrome=""
      aria-label={`Clear the picked bee (bee ${beeId})`}
      onClick={onClear}
      style={{
        position: 'absolute',
        zIndex: 3,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        minHeight: 44,
        padding: '0 14px',
        borderRadius: 'var(--r-pill)',
        border: '1.5px solid var(--hud-line)',
        background: 'var(--hud-bg)',
        boxShadow: 'var(--shadow-hud)',
        color: 'var(--hud-text)',
        fontFamily: 'var(--font-display)',
        fontWeight: 600,
        fontSize: 12,
        cursor: 'pointer',
        ...style,
      }}
    >
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>Bee {beeId}</span>
      <span aria-hidden="true" style={{ color: 'var(--hud-text-dim)' }}>
        Clear ✕
      </span>
    </button>
  );
}
